import React, { useState, useEffect, forwardRef } from 'react';
import { useOnClickOutside } from '../../hooks/useOnClickOutside';
import { emojiKeyNav } from '../../utils/emoji-nav-ally';
import { KeyDownEvent } from '../../utils/eventKey';
import './emojis.scss';
import { EMOJI_LABEL_SYMBOLS, EMOJI_LABEL_HAND, EMOJI_LABEL_PPL } from './emoji-utils'

const EM0JIS_SYMBOLS = ['1', '2', '3', '4', 'emoticon3', 'emoticon6', 'emoticon2', '8', 'emoticon8', '10', 'emoticon4', '12',
  '13', '14', '15', '16', '17', '18', '19', '20', '21', '22', '23', 'emoticon1', '25', '26', '27', '28'];

const EM0JIS_HAND = ['29', '30', '31', '32', '33', '34', '35', '36', '37', '38', '39', '40',
  '41', '42', 'emoticon7', '44', '45', '46', '47', '48', '49'];

const EM0JIS_PPL = [
  '50', '51', '52', '53', '54', '55', '56', 'emoticon5', '58', '59', '60', '61', '62', '63', '64', '65', '66', '67',
  '68', '69', '70', '71', '72', '73', '74', '75', '76', '77', '78', '79', '80', '81', '82', '83', '84', '85', '86', '87', '88', '89', '90', '91', '92',
  '93', '94', '95', '96', '97', '98', '99', '100', '101', '102', '103', '104', '105'];

const EMOJIS_LIST = [
  {
    title: 'Symbols',
    emojis: EM0JIS_SYMBOLS,
    label: EMOJI_LABEL_SYMBOLS
  },
  {
    title: 'Hand Gestures',
    emojis: EM0JIS_HAND,
    label: EMOJI_LABEL_HAND
  },
  {
    title: 'People',
    emojis: EM0JIS_PPL,
    label: EMOJI_LABEL_PPL
  },
];

const Emojis = forwardRef((props, ref) => {
  const { emojis, hideEmojiList, editorId, closeList } = props;
  const emojiID = `smiley-icons-emoji-wrapper-${editorId}`;
  const addEmojiIcon = (e, icon) => {
    e.preventDefault();
    e.stopPropagation();
    hideEmojiList(icon);
  }

  const renderIcon = (icon, index, title, altLabel) => {
    const iconPath = `${wbConfig.imageCdnUrl}/images/goal/emoticon/${icon}.png`;
    const emojiTitle = title.split(' ')[0];

    const emojiKeyHandler = (e) => {
      emojiKeyNav(e, emojiID, 11, 
        () => addEmojiIcon(e, iconPath),
        () => {
          document.querySelector(`#smiley-icon-${editorId}`)?.focus();
          closeList(false);
        })
    }

    return (
      <div className="smiley-emoji-icon" key={index}>
        <div
          role="menuitem"
          tabIndex={-1}
          onClick={e => addEmojiIcon(e, iconPath)}
          onKeyDown={(e) => emojiKeyHandler(e)}
          data-emoji-type={emojiTitle}
        >
          <img
            alt={altLabel[index]}
            src={iconPath}
          />
        </div>
      </div>
    )
  }

  const renderEmoji = (e, i) => (
    <div key={i} className="smiley-emoji-section" data-emojiwrap="true">
      <div className="smiley-emoji-title">{e.title}</div>
      <div className="smiley-icons-wrapper">
        {
          e.emojis.map((emoji, index) => renderIcon(emoji, index, e.title, e.label))
        }
      </div>
    </div>
  )

  return (
    <div ref={ref} className="smiley-list-wrapper" role="menu" id={emojiID}>
      {
        emojis.map((e, i) => renderEmoji(e, i))
      }
    </div>
  )
})

const EmojiContainer = (props) => {
  const { editorId = null, ck_editor = null } = props;
  const [showList, setShowList] = useState(false);
  const showEmoji = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setShowList(true);
  }

  useEffect(() => {
    if(showList) {
      document.querySelector(`#smiley-icons-emoji-wrapper-${editorId} [role="menuitem"]`)?.focus();
    }
  }, [editorId, showList])
  

  const hideEmojiList = (icon) => {
    if (editorId && typeof CKEDITOR.instances[editorId] !== 'undefined') {
      CKEDITOR.instances[editorId].insertHtml(`<img src="${icon}" class="smiley-editor-emoticon" style="width: 21px; height: 21px; padding:0 2px; vertical-align: middle;"/>`);
    } else if (ck_editor) {
      const content = `<img src="${icon}" class="smiley-editor-emoticon" style="width: 21px; height: 21px; padding:0 2px; vertical-align: middle;"/>`;
      const viewFragment = ck_editor.data.processor.toView( content );
      const modelFragment = ck_editor.data.toModel( viewFragment );
      ck_editor.model.insertContent( modelFragment, ck_editor.model.document.selection.getFirstPosition() );
      
      ck_editor.focus();
      ck_editor.model.change( writer => {
          writer.setSelection( writer.createPositionAt( ck_editor.model.document.getRoot(), 'end' ));
      });
      // editorId.model.change(writer => {
      //   const insertPosition = editorId.model.document.selection.getFirstPosition();
      //   writer.insertText("ck editor", insertPosition);
      // });
    }
    else if (icon) {
      const img = document.createElement('img');
      img.setAttribute('src', icon);
      img.setAttribute('class', 'smiley-editor-emoticon');
      img.setAttribute('style', 'width: 21px; height: 21px; padding:0 2px; vertical-align: middle;');
      const e = document.getElementById(editorId);
      if (e) {
        e.append(img);
        placeCaretAtEnd(e);
        if (typeof props.onChange !== 'undefined') {
          props.onChange(e);
        }
      }
    }
    setShowList(false);
  }

  const [smileyRef] = useOnClickOutside(() => {
    setShowList(false);
  });

  return (
    <div
      className="smiley-wrapper"
      key={`emojiscontainer_${editorId}`}
    >
      <div
        className="smiley-icon"
        onClick={e => showEmoji(e)}
        onKeyDown={e => KeyDownEvent(e, () => showEmoji(e))}
        role="button"
        tabIndex={0}
        id={`smiley-icon-${editorId}`}
        aria-expanded={showList}
				aria-controls={`smiley-icons-emoji-wrapper-${editorId}`}
        aria-haspopup="true"
      >
        <img
          alt="Add Emoji"
          src={`${wbConfig.imageCdnUrl}/images/goal/emoticon/50.png`}
          style={{ position: 'inherit' , top:'4px' }}
        />
      </div>
      {showList && <Emojis
        ref={smileyRef}
        key={`emojis_${editorId}`}
        emojis={EMOJIS_LIST}
        hideEmojiList={hideEmojiList}
        editorId={editorId}
        closeList={setShowList}
      />}
    </div>
  )
}

export default EmojiContainer;
